import { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/core/firebase';

interface TrainingPlan {
  rotationTemplates: string[];
  lastCompletedTemplateId?: string;
  dailyRotationTemplateIds?: string[];
  lastCompletedDailyTemplateId?: string;
}

/**
 * Manages training plan state for a client's user doc.
 * `clientUserId` is the /users doc ID (not the /clients doc ID).
 *
 * Reads subscribe directly to Firestore. Writes go through
 * `manageClientTrainingPlan` Cloud Function because trainers cannot
 * write client-owned /users docs directly under current Firestore rules.
 */
export function useTrainingPlan(clientUserId?: string) {
  const [plan, setPlan] = useState<TrainingPlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    if (!clientUserId) {
      setPlan(null);
      setLoading(false);
      setError(null);
      return;
    }

    const loadPlan = async () => {
      setLoading(true);
      setError(null);

      try {
        const getClientTrainingPlan = httpsCallable<
          { clientUserId: string },
          TrainingPlan
        >(functions, 'getClientTrainingPlan');
        const result = await getClientTrainingPlan({ clientUserId });
        if (!cancelled) {
          setPlan(result.data);
        }
      } catch (err) {
        console.error('[useTrainingPlan] Failed to load training plan:', err);
        if (!cancelled) {
          setPlan(null);
          setError(err instanceof Error ? err.message : 'Unable to load training plan.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadPlan();

    return () => {
      cancelled = true;
    };
  }, [clientUserId]);

  const callPlanFn = httpsCallable<
    {
      operation: string;
      clientUserId: string;
      templateId?: string;
      clonedTemplateId?: string;
      oldIndex?: number;
      newIndex?: number;
    },
    TrainingPlan & { clonedTemplateId?: string }
  >(functions, 'manageClientTrainingPlan');

  const applyPlanResult = (nextPlan: TrainingPlan) => {
    setPlan(nextPlan);
  };

  const addToRotation = async (templateId: string) => {
    if (!clientUserId) return;
    const result = await callPlanFn({ operation: 'addToRotation', clientUserId, templateId });
    applyPlanResult(result.data);
    return result.data.clonedTemplateId;
  };

  const removeFromRotation = async (clonedTemplateId: string) => {
    if (!clientUserId) return;
    const result = await callPlanFn({ operation: 'removeFromRotation', clientUserId, clonedTemplateId });
    applyPlanResult(result.data);
  };

  const reorderRotation = async (oldIndex: number, newIndex: number) => {
    if (!clientUserId) return;
    const result = await callPlanFn({ operation: 'reorderRotation', clientUserId, oldIndex, newIndex });
    applyPlanResult(result.data);
  };

  const addToDailyRotation = async (templateId: string) => {
    if (!clientUserId) return;
    const result = await callPlanFn({ operation: 'addToDailyRotation', clientUserId, templateId });
    applyPlanResult(result.data);
    return result.data.clonedTemplateId;
  };

  const removeFromDailyRotation = async (clonedTemplateId: string) => {
    if (!clientUserId) return;
    const result = await callPlanFn({ operation: 'removeFromDailyRotation', clientUserId, clonedTemplateId });
    applyPlanResult(result.data);
  };

  const reorderDailyRotation = async (oldIndex: number, newIndex: number) => {
    if (!clientUserId) return;
    const result = await callPlanFn({ operation: 'reorderDailyRotation', clientUserId, oldIndex, newIndex });
    applyPlanResult(result.data);
  };

  return {
    plan,
    loading,
    error,
    addToRotation,
    removeFromRotation,
    reorderRotation,
    addToDailyRotation,
    removeFromDailyRotation,
    reorderDailyRotation,
  };
}
